import { injectable } from "tsyringe";
import { BehaviorSubject } from "rxjs";
import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { Error } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { IOTSimListLookupInjection } from "../../di/IOTSimListLookupInjection";
import { IOTSimListLookupListRequestEntity } from "../../domain/entities/IOTSimListLookupListRequestEntity";
import { IIOTSimListLookupObservedState } from "../interfaces/IIOTSimListLookup";

// ─── Input ────────────────────────────────────────────────────────────────────
// Input = command streams only. No state, no loading flags.

class IOTSimListLookupInput implements Disposable {
  public fetchList = new BehaviorSubject<IOTSimListLookupListRequestEntity>({});
  public exportExcel = new BehaviorSubject<IOTSimListLookupListRequestEntity>(
    {},
  );
  public downloadTemplate = new BehaviorSubject<void>(undefined);

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.exportExcel.complete();
    this.downloadTemplate.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────
// Output = one-time events/effects only. No renderable state here.

class IOTSimListLookupOutput implements Disposable {
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<any>(null);
  public onDownloadTemplateSuccess = new BehaviorSubject<any>(null);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.onExportSuccess.complete();
    this.onDownloadTemplateSuccess.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class IOTSimListLookupViewModel extends BasedObservedViewModel<
  IOTSimListLookupInput,
  IOTSimListLookupOutput,
  IIOTSimListLookupObservedState
> {
  private useCase = IOTSimListLookupInjection.getIOTSimListLookupUseCase();

  protected override createObservedState(): IIOTSimListLookupObservedState {
    return {
      loading: false,
      exportLoading: false,
      downloadLoading: false,
      list: [],
      paging: {},
      fileId: "",
    };
  }

  constructor() {
    super(new IOTSimListLookupInput(), new IOTSimListLookupOutput());
  }

  protected override transform(): void {
    // Fetch list
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.setObservedState({ loading: true, list: [] });
        const res = await this.useCase.getIOTSimListLookupList(params);
        if (res?.data) {
          this.setObservedState({ list: res.data, paging: res.pagination, fileId: res?.file?.id });
        }
      } catch (error) {
        console.error(error);
        this.setObservedState({ list: [], paging: {} });
        this.output.onError.next(error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    // Export Excel
    this.skipFirstInitial(this.input.exportExcel, async (params) => {
      try {
        this.setObservedState({ exportLoading: true });
        const res = await this.useCase.exportIOTSimListLookupExcel(params);
        if (res) {
          this.output.onExportSuccess.next(res);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.setObservedState({ exportLoading: false });
      }
    });

    // Download Template
    this.skipFirstInitial(this.input.downloadTemplate, async () => {
      try {
        this.setObservedState({ downloadLoading: true });
        const res = await this.useCase.downloadIOTSimListLookupTemplate({ trackingField: "phoneNumber" });
        if (res) {
          this.output.onDownloadTemplateSuccess.next(res);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.setObservedState({ downloadLoading: false });
      }
    });
  }
}
